import { useState, useContext } from 'react';
import { Link, Typography, Grid, Box, Divider } from '@mui/material';
import { Loop, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Hidden from '../../../components/Hidden';
import {
    generateFormField,
    fullName,
    formatPriceByLocale,
} from '../../../lib/utils';
import {
    AppContext,
    SttButton,
    SttButtonAccept,
    SttDialog,
    SttFormText,
    SttSelectPForm,
} from '../../../sporttia/all';
import { SttInscriptionList } from '../../../sporttia/lists/SttInscriptionList';
import { SttSelectOrAddUser } from '../../../sporttia/selectors/SttSelectOrAddUser';

type User = {
    id: number | null;
    name: string;
    surname1?: string;
    surname2?: string;
};

type Inscription = {
    idUser: number | null;
    name: string;
    surname1?: string;
    surname2?: string;
    idCat: number;
    form: Record<string, string> | null;
    price: number;
};

export default function ModalSignup({
    event,
    team,
    category,
    onClose,
    onSaveInscription,
    onSaveTeam,
    idMship,
}: any) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [user, setUser] = useState<User>({
        id: cxt.user?.role === 'SPORTCENTER' ? null : (cxt.user?.id ?? null),
        name: cxt.user?.role === 'SPORTCENTER' ? '' : fullName(cxt.user),
        surname1: cxt.user?.surname1,
        surname2: cxt.user?.surname2 ?? undefined,
    });

    const [pending, setPending] = useState<Inscription[]>([]);
    const [paymentForm, setPaymentForm] = useState('PURSE');
    const [formValues, setFormValues] = useState(event.form);
    const [someoneElse, setSomeoneElse] = useState(
        cxt.user?.role === 'SPORTCENTER',
    );
    const [teamName, setTeamName] = useState(team ? team.name : '');
    const [creatingTeam] = useState(!team);
    const [idTeam] = useState(team ? team.id : null);

    // const [capacity, setCapacity] = useState(!!event.teamable ? category.maxTeams !== 0 : (category.maxParticipants !== 0 || event.maxParticipants !== 0));
    const [availableSlots, setAvailableSlots] = useState(
        calculateAvailableSlots(),
    );
    const inscribeOtherLink = (
        <Link
            onClick={() => {
                setSomeoneElse(true);
                setUser({ id: 0, name: '', surname1: '', surname2: '' });
            }}
            sx={{ cursor: 'pointer' }}
        >
            <Typography
                variant="body2"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
                <Loop style={{ fontSize: 16 }} />
                <span>{t('page.activity.signUpAnotherPerson')}</span>
            </Typography>
        </Link>
    );

    const inscribeMeLink = (
        <Link
            onClick={() => {
                setSomeoneElse(false);
                setUser({
                    id: cxt.user?.id ?? null,
                    name: fullName(cxt.user),
                    surname1: cxt.user?.surname1,
                    surname2: cxt.user?.surname2 ?? undefined,
                });
            }}
            sx={{ cursor: 'pointer' }}
        >
            <Typography
                variant="body2"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
                <Loop style={{ fontSize: 16 }} />
                <span>{t('activity.signMeUp')}</span>
            </Typography>
        </Link>
    );

    function calculateAvailableSlots() {
        // Determine which 'available slots' config, in whole event or this specific category, is more restrictive.
        let result;
        if (event.teamable) {
            result =
                typeof event.maxPlayers === 'number' && event.maxPlayers !== 0
                    ? event.maxPlayers -
                      (team?.participants?.length
                          ? team?.participants?.length
                          : 0)
                    : null;
        } else if (
            category.maxParticipants === 0 &&
            event.maxParticipants === 0
        ) {
            result = null;
        } else {
            const catSlots =
                category.maxParticipants !== 0
                    ? category.maxParticipants - category.numParticipants
                    : null;
            const eventSlots =
                event.maxParticipants !== 0
                    ? event.maxParticipants - event.numParticipants
                    : null;

            if (catSlots === null && eventSlots === null) {
                result = null;
            } else {
                if (catSlots === null) {
                    return eventSlots;
                }
                if (eventSlots === null) {
                    return catSlots;
                }
                result = catSlots < eventSlots ? catSlots : eventSlots;
            }
        }

        return result;
    }

    function handleFormFieldChange({ name, value }: any) {
        setFormValues(
            formValues.map((question: any) =>
                question.id === parseInt(name)
                    ? { ...question, answer: { value } }
                    : question,
            ),
        );
    }

    function clearForm() {
        setFormValues(
            formValues.map((question: any) => ({
                ...question,
                answer: { value: '' },
            })),
        );
    }

    function parseForm(formFields: any): Record<string, string> {
        const result: Record<string, string> = {};
        formFields.forEach((field: any) => {
            result[field.id] = field.answer ? field.answer.value : '';
        });

        return result;
    }

    function addCurrentToPending() {
        if (user.id === 0 && user.name === '') {
            cxt.showMessage('E', t('event.noPlayerName'));
            return;
        }

        if (
            pending.find(
                (insc: any) =>
                    (user.id && insc.id === user.id) ||
                    fullName(insc) === fullName(user),
            )
        ) {
            cxt.showMessage('E', t('messages.userAlreadyOnList'));
            return;
        }

        if (form && form.length > 0) {
            let valid = true;
            let missing;
            for (let i = 0; i < formValues.length; i++) {
                if (
                    formValues[i].mandatory &&
                    (!formValues[i].answer || !formValues[i].answer.value)
                ) {
                    valid = false;
                    missing = formValues[i];
                    break;
                }
            }

            if (!valid) {
                cxt.showMessage(
                    'E',
                    `${t('labels.mandatory')}: ${missing.name}`,
                );
                return;
            }
        }

        const params = {
            idUser: user.id,
            name: user.name,
            surname1: user.surname1,
            surname2: user.surname2,
            idCat: category.id,
            form: form && form.length > 0 ? parseForm(formValues) : null,
            price: category.enrollment,
        };

        setPending(pending.concat(params));

        if (someoneElse) {
            setUser({ id: 0, name: '', surname1: '', surname2: '' });
        }

        if (formValues) {
            clearForm();
        }

        // Add to local count
        if (availableSlots !== null) {
            setAvailableSlots(availableSlots - 1);
        }
    }

    function deletePendingInsc(inscription: any) {
        setPending(
            pending.filter((insc: any) => {
                if (insc.idUser === 0) {
                    return insc.name !== inscription.name;
                }
                return insc.idUser !== inscription.idUser;
            }),
        );

        // Delete from local count
        if (availableSlots !== null) {
            setAvailableSlots(availableSlots + 1);
        }
    }

    function save() {
        if (!event.teamable) {
            if (!pending || pending.length === 0) {
                cxt.showMessage('E', t('messages.emptyList'));
                return;
            }

            onSaveInscription?.({
                paymentForm,
                participants: pending.map((participant: any) => ({
                    idUser: participant.idUser,
                    name: participant.name,
                    surname1: participant.surname1,
                    surname2: participant.surname2,
                    idCat: participant.idCat,
                    form: participant.form,
                })),
            });
        } else {
            if (!idTeam && teamName === '') {
                cxt.showMessage('E', t('event.teamNameEmpty'));
                return;
            }

            onSaveTeam?.({
                teamName,
                idTeam,
                paymentForm,
                participants: pending.map((participant: any) => ({
                    idUser: participant.idUser,
                    name: participant.name,
                    surname1: participant.surname1,
                    surname2: participant.surname2,
                    form: participant.form,
                    admin: cxt.user && participant.idUser === cxt.user?.id,
                })),
            });
        }
    }

    if (!event) {
        return true;
    }

    const { form } = event;

    return (
        <SttDialog
            open
            onClose={onClose}
            title={t('event.inscription')}
            maxWidth="md"
            content={
                <Grid container spacing={3}>
                    {/* LEFT SIDE: Category details and inscription form */}

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2">
                            {t('page.event.signupDescription')}
                        </Typography>
                        <Box
                            mt={3}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <div>
                                <Typography variant="caption">
                                    {t('event.category')}
                                </Typography>
                                <Typography variant="body1">
                                    {category.name}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant="caption">
                                    {t('event.inscription')}
                                </Typography>
                                {category.enrollment ? (
                                    <Box>
                                        <Typography variant="body1">
                                            {formatPriceByLocale(
                                                category?.enrollment,
                                                category?.enrollmentShape
                                                    ?.currency,
                                                category?.enrollmentShape
                                                    ?.locale,
                                            )}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography variant="body1">
                                            {' '}
                                            {t('payment.free')}
                                        </Typography>
                                    </Box>
                                )}
                            </div>
                        </Box>
                        <Box my={3}>
                            <Divider />
                        </Box>
                        {!!event.teamable && (
                            <Box mb={3}>
                                <Box
                                    mb={1}
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Typography variant="body2">
                                        <b>
                                            {creatingTeam
                                                ? t('event.newTeam')
                                                : t('event.team')}
                                            :
                                        </b>
                                    </Typography>
                                </Box>
                                {creatingTeam ? (
                                    <Box>
                                        <SttFormText
                                            defVal={teamName}
                                            placeholder={t(
                                                'app.event.teamName',
                                            )}
                                            onChange={({ value }: any) =>
                                                setTeamName(value)
                                            }
                                        />
                                    </Box>
                                ) : (
                                    <Box>
                                        <SttFormText
                                            disabled
                                            defVal={teamName}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                        <Box mb={3}>
                            <Box
                                mb={1}
                                display="flex"
                                justifyContent="space-between"
                            >
                                <Typography variant="body2">
                                    <b>{t('page.activity.personToSignUp')}:</b>
                                </Typography>
                                {cxt.user?.role !== 'SPORTCENTER' && (
                                    <Hidden xsDown>
                                        {someoneElse
                                            ? inscribeMeLink
                                            : inscribeOtherLink}
                                    </Hidden>
                                )}
                            </Box>
                            {cxt.user?.role === 'SPORTCENTER' || someoneElse ? (
                                <SttSelectOrAddUser
                                    defVal={user}
                                    name="user"
                                    caption={t('labels.user')}
                                    onChange={(user: User) => setUser(user)}
                                    onEnterKey={addCurrentToPending}
                                    idSC={event.sc.id}
                                    idMship={idMship}
                                />
                            ) : (
                                <SttFormText
                                    disabled={user.id !== null}
                                    name="person"
                                    defVal={user.name} // Nota: este .name es solo cosmético, su valor será el fullName del usuario
                                    placeholder={
                                        !user.id ? t('user.nameAndSurname') : ''
                                    }
                                    // onChange={({ inputName, value }: any) => setUser({name: value, id: null})}
                                />
                            )}
                            {cxt.user?.role !== 'SPORTCENTER' && (
                                <Hidden smUp>
                                    <Box mt={1}>
                                        {someoneElse
                                            ? inscribeMeLink
                                            : inscribeOtherLink}
                                    </Box>
                                </Hidden>
                            )}
                        </Box>
                        {form && form.length > 0 && (
                            <>
                                <Box
                                    mb={1}
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Typography variant="body2">
                                        <b>{t('page.homeA.ev.2')}:</b>
                                    </Typography>
                                </Box>
                                {formValues.map((item: any, i: any) => (
                                    <Box mb={3} key={i}>
                                        <>
                                            {generateFormField(
                                                item,
                                                handleFormFieldChange,
                                            )}
                                            {!!item.mandatory && (
                                                <span
                                                    style={{
                                                        color: '#f44336',
                                                        fontSize: '0.875rem',
                                                    }}
                                                >{`(${t(
                                                    'labels.mandatory',
                                                )})`}</span>
                                            )}
                                        </>
                                    </Box>
                                ))}
                            </>
                        )}
                        <Box display="flex" justifyContent="flex-end">
                            <SttButton
                                startIcon={<Add />}
                                caption={t('ui.add')}
                                color={
                                    availableSlots !== null &&
                                    availableSlots <= 0
                                        ? 'primary'
                                        : 'inherit' /* if button is disabled, change the color to 'primary' (it looks better when disabled) */
                                }
                                variant="contained"
                                disabled={
                                    availableSlots !== null &&
                                    availableSlots <= 0
                                }
                                onClick={addCurrentToPending}
                            />
                        </Box>
                        {availableSlots !== null && availableSlots <= 0 && (
                            <Box display="flex" justifyContent="flex-end">
                                <Typography
                                    variant="caption"
                                    sx={{ color: '#f44336' }}
                                >
                                    {t('page.event.maxUsersSignup')}
                                </Typography>
                            </Box>
                        )}
                    </Grid>

                    {/* RIGHT SIDE: List of pendings inscriptions */}

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="h6">
                            {t('page.event.userForSignup')}:
                        </Typography>

                        {/* List of pending incriptions */}
                        <SttInscriptionList
                            hidePrice={!creatingTeam}
                            inscriptions={pending}
                            onDelete={deletePendingInsc}
                        />

                        {/* CAPACITY */}
                        {availableSlots !== null && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color:
                                        availableSlots === 0
                                            ? '#f44336'
                                            : 'inherit',
                                }}
                            >
                                {`${t(
                                    'pages.events.availableSlots',
                                )}: ${availableSlots}`}
                            </Typography>
                        )}

                        {/* PAYMENT */}
                        {category.enrollment > 0 &&
                            pending.length > 0 &&
                            creatingTeam && (
                                <>
                                    <Box mt={3}>
                                        <Divider />
                                    </Box>
                                    <Box mt={3}>
                                        {/* <Box mb={1} display={"flex"} justifyContent={"space-between"}>
									<Typography variant={"body2"}>
										<b>{t('PaymentForm')}</b>
										<span>({`${t('page.activity.signUpPayment')}, ${formatPrice(category.enrollment, t('coin'))}`})</span></Typography>
								</Box> */}
                                        <Box
                                            mb={3}
                                            display="flex"
                                            justifyContent="space-between"
                                        >
                                            <Typography
                                                variant="body2"
                                                style={{ fontSize: 16 }}
                                            >
                                                <b>{`${t('payment.total')} (${t(
                                                    'event.enrollment',
                                                )})`}</b>
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                style={{ fontSize: 18 }}
                                            >
                                                <b>
                                                    {event.teamable
                                                        ? `${formatPriceByLocale(
                                                              category
                                                                  ?.enrollmentShape
                                                                  ?.price,
                                                              category
                                                                  ?.enrollmentShape
                                                                  ?.currency,
                                                              category
                                                                  ?.enrollmentShape
                                                                  ?.locale,
                                                          )}`
                                                        : `${formatPriceByLocale(
                                                              pending.reduce(
                                                                  (res, insc) =>
                                                                      res +
                                                                      insc.price,
                                                                  0,
                                                              ),
                                                              category
                                                                  ?.enrollmentShape
                                                                  ?.currency,
                                                              category
                                                                  ?.enrollmentShape
                                                                  ?.locale,
                                                          )}`}
                                                </b>
                                                {/* <b>{`${formatPrice(pending[0].price, t('coin'))}`}</b> */}
                                            </Typography>
                                        </Box>
                                        <SttSelectPForm
                                            caption=""
                                            defVal={paymentForm}
                                            activePFs={event.paymentForms}
                                            onChange={({ value }: any) =>
                                                setPaymentForm(value)
                                            }
                                        />
                                    </Box>
                                </>
                            )}
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Box display="flex" justifyContent="flex-end">
                            <SttButtonAccept
                                caption={t('activity.signUp')}
                                onClick={save}
                            />
                        </Box>
                    </Grid>
                </Grid>
            }
        />
    );
}
